"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { useTranslation } from "react-i18next"
import toast from "react-hot-toast"
import providersService from "../../services/providersService"

import {
  X,
  Pencil,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  Users,
  Percent,
  Globe,
} from "lucide-react"

// Optional: same palette as your table
const getPlatformBadge = (p) => {
  const common =
    "inline-flex items-center px-2 py-0.5 rounded-xl text-xs font-medium"
  switch (p) {
    case "Facebook":
      return `${common} bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300`
    case "Google":
      return `${common} bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300`
    case "TikTok":
      return `${common} bg-neutral-900 text-white dark:bg-neutral-700 dark:text-white`
    case "Instagram":
      return `${common} bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300`
    default:
      return `${common} bg-neutral-50 text-neutral-700 dark:border-neutral-900 dark:bg-neutral-900/20 dark:text-neutral-300`
  }
}

const getPlatformIcon = (platform) => {
  switch (platform.toLowerCase()) {
    case "facebook":
    case "meta":
      return (
        <svg width="18" height="18" viewBox="0 0 48 48">
          <path fill="#0081fb" d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
            c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38l-0.19,0.28l-0.11,0.16
            c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83l-0.28,0.51l-0.08,0.13l-0.21,0.37l-1.24,2.19
            c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17
            c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09
            l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57l0.31-0.42l0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1
            c1.85-1.61,3.84-2.5,5.85-2.5c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5
            C47,29.03,47,29.19,47,29.36z"/>
        </svg>
      )
    case "google":
      return (
        <svg width="18" height="18" viewBox="0 0 48 48">
          <polygon fill="#ffc107" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75"></polygon>
          <path fill="#1e88e5" d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22
            c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"/>
          <circle cx="11.5" cy="34.5" r="6.5" fill="#43a047"></circle>
        </svg>
      )
    case "tiktok":
      return (
        <svg width="18" height="18" viewBox="0 0 48 48">
          <path fill="#212121" d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191C42,39.804,39.804,42,37.096,42H10.904
            C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z"/>
          <path fill="#fff" d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157c-1.164-0.248-2.194-0.856-2.969-1.701
            c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379c-1.102,0-2.081-0.525-2.701-1.338
            c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391c0.359,0,0.705,0.056,1.03,0.159V21.38
            c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183c1.18,0.792,2.601,1.254,4.129,1.254
            c4.096,0,7.417-3.319,7.417-7.413L28.034,19.63z"/>
        </svg>
      )
    case "instagram":
      // keeping it simple, you can add a proper glyph later
      return <Users size={16} />
    default:
      return <Globe size={16} className="text-neutral-800 dark:text-neutral-400" />
  }
}



const ProviderDetailsModal = ({ isOpen, onClose, providerId, onEdit }) => {
  const { t } = useTranslation()
  const [provider, setProvider] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && providerId) fetchProvider()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, providerId])

  const fetchProvider = async () => {
    try {
      setLoading(true)
      const res = await providersService.get(providerId)
      if (res.data?.statusCode === 200) {
        setProvider(res.data.data)
      } else {
        toast.error(t("Failed to load provider data"))
      }
    } catch (e) {
      console.error("Failed to load provider:", e)
      toast.error(t("Failed to load provider data"))
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return t("Not available")
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date)
  }

  const typeDisplay = (tp) => {
    if (!tp) return t("Unknown")
    // your schema sometimes uses array; show first if array
    if (Array.isArray(tp)) return tp[0] || t("Unknown")
    return String(tp)
  }

  const feePct = (platform) => {
    const f = (provider?.fees || []).find((x) => x.platform === platform)
    return typeof f?.feePercentage === "number" ? f.feePercentage : null
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-neutral-900 to-neutral-800 dark:from-black dark:to-neutral-900 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-white">
                      {t("Provider Details")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {provider && (
                    <div className="mt-3 text-white">
                      <h2 className="text-xl font-medium">{provider.name}</h2>

                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {/* Type */}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-xl text-xs font-medium bg-green-500/20 text-green-100">
                          {typeDisplay(provider.type)}
                        </span>

                        {/* Platforms count */}
                        <span className="text-xs text-white/80 inline-flex items-center gap-1">
                          <Users size={12} />
                          {(provider.platforms || []).length} {t("platforms")}
                        </span>

                        {/* created/updated */}
                        <span className="text-xs text-white/70">
                          {t("Created")}: {formatDate(provider.createdAt)}
                        </span>
                        {provider.updatedAt && (
                          <span className="text-xs text-white/70">
                            {t("Updated")}: {formatDate(provider.updatedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 size={24} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : !provider ? (
                    <div className="text-center py-6">
                      <AlertCircle size={32} className="mx-auto mb-3 text-red-500" />
                      <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-2">
                        {t("Provider Not Found")}
                      </h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                        {t("The selected provider could not be found. Please try again.")}
                      </p>
                      <button
                        onClick={onClose}
                        className="px-3 py-1.5 bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-white rounded-xl hover:bg-neutral-300 dark:hover:bg-neutral-600"
                      >
                        {t("Close")}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {/* Contact */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{t("Email")}</p>
                          <p className="text-sm text-neutral-900 dark:text-white inline-flex items-center gap-2">
                            <Mail size={14} className="text-neutral-500 dark:text-neutral-400" />
                            {provider.email}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{t("Phone")}</p>
                          <p className="text-sm text-neutral-900 dark:text-white inline-flex items-center gap-2">
                            <Phone size={14} className="text-neutral-500 dark:text-neutral-400" />
                            {provider.phone || t("Not provided")}
                          </p>
                        </div>

                        {/* Platforms */}
                        <div className="space-y-1 md:col-span-2">
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{t("Platforms")}</p>
                          <div className="flex flex-wrap gap-2">
                            {(provider.platforms || []).map((pl) => (
                              <span key={pl} className={getPlatformBadge(pl)}>
                                {pl}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Fees */}
                      <div className="pt-2">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white mb-3">
                          {t("Platform Fees")}
                        </p>

                        {Array.isArray(provider.fees) && provider.fees.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {provider.platforms?.map((pl) => {
                              const pct = feePct(pl)
                              return (
                                <div
                                  key={pl}
                                  className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 p-3 rounded-xl border border-neutral-100 dark:border-neutral-900 dark:bg-neutral-900/30"
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    {getPlatformIcon(pl)}
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{pl}</p>
                                  </div>
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white inline-flex items-center gap-1">
                                    <Percent size={14} className="opacity-70" />
                                    {typeof pct === "number" ? `${pct}%` : t("Not set")}
                                  </p>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            {t("No fees set for this provider.")}
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {provider.notes && (
                        <div className="pt-2">
                          <p className="text-sm font-medium text-neutral-900 dark:text-white mb-2">{t("Notes")}</p>
                          <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 p-3 rounded-xl">
                            <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-line">
                              {provider.notes}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-4 flex justify-end">
                  {provider && (
                    <button
                      type="button"
                      onClick={() => onEdit?.(provider._id)}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-neutral-800 rounded-xl hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500/30 mr-3 dark:bg-neutral-700 dark:hover:bg-neutral-600"
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      {t("Edit")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                  >
                    {t("Close")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ProviderDetailsModal
